import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, SmartDataTable, getStatusBadge, Modal } from '@/backoffice/components/ui';
import { TrackingMap } from '@/backoffice/components/tracking/TrackingMap';
import { OrderChatPanel } from '@/backoffice/components/messaging/OrderChatPanel';
import { useOrderTracking } from '@/backoffice/hooks/useOrderTracking';
import { fetchAdminOrders, createDispute, type AdminOrderSummary } from '@/domains/admin/services/admin.service';
import { formatCurrency, formatRelativeTime } from '@/backoffice/utils';
import { ArrowRight, Gavel, Loader2, MapPin } from 'lucide-react';

type FilterStatus = 'all' | 'pending' | 'in_progress' | 'delivered' | 'cancelled';

export function AdminOrdersPage() {
  const [orders, setOrders] = useState<AdminOrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [showDetails, setShowDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<AdminOrderSummary | null>(null);
  const [creatingDispute, setCreatingDispute] = useState(false);
  const [detailTab, setDetailTab] = useState<'info' | 'tracking' | 'chat'>('info');

  const { tracking, pickup, dropoff, driver, loading: trackingLoading, live } = useOrderTracking(
    selectedOrder?.status === 'in_progress' ? selectedOrder.id : null,
  );

  const load = (status?: string) => {
    setLoading(true);
    fetchAdminOrders(status)
      .then(setOrders)
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(filter === 'all' ? undefined : filter);
  }, [filter]);

  const handleOpenDispute = async () => {
    if (!selectedOrder) return;
    setCreatingDispute(true);
    try {
      await createDispute({
        order_request_id: selectedOrder.id,
        reason: 'Litige ouvert par administrateur',
        description: `Litige pour la commande ${selectedOrder.code}`,
      });
      setShowDetails(false);
    } finally {
      setCreatingDispute(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="admin" title="Commandes">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="admin" title="Commandes">
      <div className="flex gap-2 mb-4 flex-wrap">
        {(['all', 'pending', 'in_progress', 'delivered', 'cancelled'] as FilterStatus[]).map((f) => (
          <Button key={f} variant={filter === f ? 'primary' : 'outline'} size="sm" onClick={() => setFilter(f)}>
            {f === 'all' ? 'Toutes' : f.replace('_', ' ')}
          </Button>
        ))}
      </div>

      <Card>
        <SmartDataTable
          data={orders}
          columns={[
            { key: 'code', title: 'ID', render: (item) => <span className="font-mono text-sm">{item.code}</span> },
            {
              key: 'client_name',
              title: 'Client',
              render: (item) => (
                <div>
                  <p className="font-medium">{item.client_name ?? '—'}</p>
                  <p className="text-xs text-gray-500">{item.client_phone}</p>
                </div>
              ),
            },
            {
              key: 'route',
              title: 'Trajet',
              render: (item) => (
                <div className="flex items-center gap-2 text-sm">
                  <span>{item.departure_zone ?? '—'}</span>
                  <ArrowRight className="w-3 h-3" />
                  <span>{item.arrival_zone ?? '—'}</span>
                </div>
              ),
            },
            { key: 'delivery_type', title: 'Type', render: (item) => <Badge variant="primary" size="sm">{item.delivery_type ?? '—'}</Badge> },
            { key: 'status', title: 'Statut', render: (item) => getStatusBadge(item.status) },
            { key: 'created_at', title: 'Date', render: (item) => item.created_at ? formatRelativeTime(item.created_at) : '—' },
            { key: 'price', title: 'Montant', render: (item) => <span className="font-semibold">{formatCurrency(item.price)}</span> },
          ]}
          keyExtractor={(item) => String(item.id)}
          onView={(order) => { setSelectedOrder(order); setShowDetails(true); }}
          emptyMessage="Aucune commande"
        />
      </Card>

      <Modal isOpen={showDetails} onClose={() => { setShowDetails(false); setDetailTab('info'); }} title="Détail commande" size="lg">
        {selectedOrder && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-mono text-lg">{selectedOrder.code}</h4>
              {getStatusBadge(selectedOrder.status)}
            </div>

            <div className="flex gap-2">
              {(['info', 'tracking', 'chat'] as const).map((tab) => (
                <Button key={tab} size="sm" variant={detailTab === tab ? 'primary' : 'outline'} onClick={() => setDetailTab(tab)}>
                  {tab === 'info' ? 'Infos' : tab === 'tracking' ? 'Suivi' : 'Messages'}
                </Button>
              ))}
            </div>

            {detailTab === 'info' && (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-700 font-medium">COLLECTE</p>
                    <p className="font-medium">{selectedOrder.departure_address}</p>
                    <p className="text-sm text-gray-600">{selectedOrder.departure_zone}</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-xs text-red-700 font-medium">LIVRAISON</p>
                    <p className="font-medium">{selectedOrder.arrival_address}</p>
                    <p className="text-sm text-gray-600">{selectedOrder.arrival_zone}</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Livreur</p><p className="font-medium">{selectedOrder.driver_name ?? 'Non assigné'}</p></div>
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Poids</p><p className="font-medium">{selectedOrder.package_weight ?? 0} kg</p></div>
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Montant</p><p className="font-bold text-emerald-600">{formatCurrency(selectedOrder.price)}</p></div>
                </div>
              </>
            )}

            {detailTab === 'tracking' && (
              <div className="space-y-3">
                {selectedOrder.status !== 'in_progress' ? (
                  <p className="text-sm text-gray-500">Suivi GPS disponible lorsque la commande est en cours de livraison.</p>
                ) : trackingLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
                ) : (
                  <>
                    {tracking?.eta_minutes != null && (
                      <div className="p-3 bg-blue-50 rounded-lg text-sm space-y-1">
                        <p className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-blue-600" />
                          ETA : ~{tracking.eta_minutes} min ({tracking.distance_km} km)
                          {tracking.routing_source === 'osrm' && <span className="text-xs text-blue-500">· itinéraire routier</span>}
                        </p>
                        {live && <p className="text-xs text-emerald-600">Suivi en temps réel</p>}
                      </div>
                    )}
                    <div className="h-72">
                      <TrackingMap pickup={pickup} dropoff={dropoff} driver={driver} />
                    </div>
                  </>
                )}
              </div>
            )}

            {detailTab === 'chat' && <OrderChatPanel orderId={selectedOrder.id} />}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDetails(false)}>Fermer</Button>
              <Button variant="danger" leftIcon={<Gavel className="w-4 h-4" />} isLoading={creatingDispute} onClick={handleOpenDispute}>
                Ouvrir un litige
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}
